const ACCESS_TOKEN_KEY = "authToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const USER_KEY = "user";

function isApiRequest(url) {
    if (!url) {
        return false;
    }
    if (url.startsWith("/api/")) {
        return true;
    }
    const apiBase = process.env.REACT_APP_API_URI;
    if (!apiBase) {
        return false;
    }
    return url.startsWith(apiBase);
}

function isRefreshRequest(url) {
    return Boolean(url && url.includes("/api/auth/refresh"));
}

function buildRequestUrl(input) {
    return typeof input === "string" ? input : input?.url;
}

function clearAuthStorage() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
}

export function setupAuthFetch() {
    if (typeof window === "undefined" || window.__authFetchSetup) {
        return;
    }

    const originalFetch = window.fetch.bind(window);
    let refreshPromise = null;

    const refreshAccessToken = async () => {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
            return null;
        }

        const apiBase = process.env.REACT_APP_API_URI || "";
        const response = await originalFetch(`${apiBase}/api/auth/refresh`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
            clearAuthStorage();
            return null;
        }

        const data = await response.json();
        if (!data?.token) {
            clearAuthStorage();
            return null;
        }

        localStorage.setItem(ACCESS_TOKEN_KEY, data.token);
        if (data.refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        }
        if (data.user) {
            localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        }
        return data.token;
    };

    const getRefreshedAccessToken = async () => {
        if (!refreshPromise) {
            refreshPromise = refreshAccessToken().finally(() => {
                refreshPromise = null;
            });
        }
        return refreshPromise;
    };

    window.fetch = async (input, init = {}) => {
        const requestUrl = buildRequestUrl(input);
        if (!isApiRequest(requestUrl)) {
            return originalFetch(input, init);
        }

        const firstHeaders = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
        const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (accessToken && !firstHeaders.has("Authorization")) {
            firstHeaders.set("Authorization", `Bearer ${accessToken}`);
        }

        const firstResponse = await originalFetch(input, {
            ...init,
            headers: firstHeaders,
        });

        if (firstResponse.status !== 401 || isRefreshRequest(requestUrl)) {
            return firstResponse;
        }

        const newAccessToken = await getRefreshedAccessToken();
        if (!newAccessToken) {
            return firstResponse;
        }

        const retryHeaders = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
        retryHeaders.set("Authorization", `Bearer ${newAccessToken}`);

        return originalFetch(input, {
            ...init,
            headers: retryHeaders,
        });
    };

    window.__authFetchSetup = true;
}
